/**
 * Bug Reporter — in-memory ring buffers for API calls and console errors.
 *
 * Wired into the central `request()` in `./api.ts` (alongside the existing
 * dev-only `apiLogger`) and into a `console.error` wrapper installed at
 * app boot via `initBugReportBuffer()` in `main.tsx`.
 *
 * Gated by `VITE_APP_ENV` so the buffers stay empty (zero memory cost)
 * outside of local / QA / UAT environments.
 */

import type { ApiLogPayload } from './apiLogger'

// Environments where the reporter works at all (buffers fill, submit allowed).
const ENABLED_ENVS = ['local', 'qa', 'uat']
// Environments where the floating UI (topbar bug icon) is shown. In UAT the
// reporter is enabled but hidden — reachable only via the Ctrl+Shift+B hotkey —
// so it stays invisible to end users while testers who know the shortcut can
// still file bugs. The shortcut is announced in the console at app boot.
const UI_VISIBLE_ENVS = ['local', 'qa']
const MAX_API_LOGS = 25
const MAX_CONSOLE_ERRORS = 25
const MAX_NAVIGATION = 25
const MAX_INTERACTIONS = 25

const appEnv = String(import.meta.env.VITE_APP_ENV ?? '').toLowerCase()
const enabled = ENABLED_ENVS.includes(appEnv)
const uiVisible = UI_VISIBLE_ENVS.includes(appEnv)

// The page the user was on before entering the SPA (external referrer),
// captured once at module load before any client-side navigation happens.
const entryReferrer =
  typeof document !== 'undefined' ? document.referrer || '' : ''

export function getEntryReferrer(): string {
  return entryReferrer
}

export function isBugReportEnabled(): boolean {
  return enabled
}

/** Whether the always-visible launcher affordance (topbar icon) should render. */
export function isBugReportUiVisible(): boolean {
  return uiVisible
}

export function bugReportEnv(): string {
  return appEnv || 'unknown'
}

const apiLogs: ApiLogPayload[] = []

export function pushBugReportApiLog(payload: ApiLogPayload): void {
  if (!enabled) return
  apiLogs.push(payload)
  if (apiLogs.length > MAX_API_LOGS) {
    apiLogs.splice(0, apiLogs.length - MAX_API_LOGS)
  }
}

export function getBufferedApiLogs(): ApiLogPayload[] {
  return apiLogs.slice()
}

export function clearBufferedApiLogs(): void {
  apiLogs.length = 0
}

export interface BufferedConsoleError {
  timestamp: string
  message: string
  stack?: string
}

const consoleErrors: BufferedConsoleError[] = []

function recordConsoleError(args: unknown[]): void {
  let message = ''
  let stack: string | undefined

  for (const a of args) {
    if (a instanceof Error) {
      message = message ? `${message}\n${a.message}` : a.message
      stack = a.stack
    } else if (typeof a === 'string') {
      message = message ? `${message} ${a}` : a
    } else {
      try {
        message = `${message} ${JSON.stringify(a)}`.trim()
      } catch {
        message = `${message} [unserialisable]`.trim()
      }
    }
  }

  consoleErrors.push({
    timestamp: new Date().toISOString(),
    message: message || '(empty error)',
    stack,
  })
  if (consoleErrors.length > MAX_CONSOLE_ERRORS) {
    consoleErrors.splice(0, consoleErrors.length - MAX_CONSOLE_ERRORS)
  }
}

// --- Navigation history (SPA route changes) -------------------------------
export interface NavigationEntry {
  timestamp: string
  url: string // pathname + search + hash
  path: string // pathname only
}

const navigationHistory: NavigationEntry[] = []

function recordNavigation(): void {
  if (!enabled || typeof window === 'undefined') return
  const path = window.location.pathname
  const url = path + window.location.search + window.location.hash
  const last = navigationHistory[navigationHistory.length - 1]
  if (last && last.url === url) return // ignore no-op / repeated entries
  navigationHistory.push({ timestamp: new Date().toISOString(), url, path })
  if (navigationHistory.length > MAX_NAVIGATION) {
    navigationHistory.splice(0, navigationHistory.length - MAX_NAVIGATION)
  }
}

export function getBufferedNavigationHistory(): NavigationEntry[] {
  return navigationHistory.slice()
}

export function clearBufferedNavigationHistory(): void {
  navigationHistory.length = 0
}

// --- Interaction trail (clicks on buttons / links) ------------------------
export interface InteractionEntry {
  timestamp: string
  path: string // route the click happened on
  label: string // human-readable description of the element
  tag: string
  href?: string | null
}

const interactions: InteractionEntry[] = []

/**
 * Describe the actionable element a click landed on, walking up a few levels
 * to the nearest button / link. Deliberately avoids reading values of text
 * inputs so we never capture what a user typed (passwords, PII, etc.).
 */
function describeClickTarget(target: EventTarget | null): InteractionEntry | null {
  let node = target instanceof Element ? target : null
  for (let depth = 0; node && depth < 5; depth++, node = node.parentElement) {
    const tag = node.tagName.toLowerCase()
    const role = node.getAttribute('role')
    const inputType = tag === 'input' ? (node as HTMLInputElement).type : null
    const isButtonLike =
      tag === 'button' ||
      tag === 'a' ||
      role === 'button' ||
      role === 'link' ||
      role === 'menuitem' ||
      role === 'tab' ||
      (tag === 'input' && (inputType === 'submit' || inputType === 'button'))
    const ariaLabel = node.getAttribute('aria-label') || node.getAttribute('title')

    if (!isButtonLike && !ariaLabel) continue

    // Prefer an explicit label; fall back to visible text only for button-like
    // elements (never a text input's value).
    let label = (ariaLabel || '').trim()
    if (!label && isButtonLike) {
      if (tag === 'input') label = (node as HTMLInputElement).value || ''
      else label = (node as HTMLElement).innerText || ''
    }
    label = label.replace(/\s+/g, ' ').trim().slice(0, 80)

    return {
      timestamp: new Date().toISOString(),
      path: window.location.pathname,
      label: label || `<${tag}>`,
      tag,
      href: tag === 'a' ? (node as HTMLAnchorElement).getAttribute('href') : null,
    }
  }
  return null
}

export function getBufferedInteractions(): InteractionEntry[] {
  return interactions.slice()
}

export function clearBufferedInteractions(): void {
  interactions.length = 0
}

let consolePatched = false

export function initBugReportBuffer(): void {
  if (!enabled || consolePatched) return
  consolePatched = true

  const originalError = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    try {
      recordConsoleError(args)
    } catch {
      // never let the buffer break the app
    }
    originalError(...args)
  }

  window.addEventListener('error', (e) => {
    recordConsoleError([e.error ?? e.message])
  })
  window.addEventListener('unhandledrejection', (e) => {
    recordConsoleError([e.reason ?? '(unhandled rejection)'])
  })

  // Track SPA navigation by wrapping the History API (react-router uses it) and
  // listening for back/forward, so a report shows the trail of pages the user
  // moved through to reach the current one.
  const patchHistory = (method: 'pushState' | 'replaceState') => {
    const original = history[method]
    history[method] = function (this: History, ...args: unknown[]) {
      const result = original.apply(this, args as Parameters<History['pushState']>)
      try {
        recordNavigation()
      } catch {
        // never let tracking break navigation
      }
      return result
    }
  }
  patchHistory('pushState')
  patchHistory('replaceState')
  window.addEventListener('popstate', () => {
    try {
      recordNavigation()
    } catch {
      /* noop */
    }
  })
  recordNavigation() // seed with the entry page

  // Track clicks on buttons/links so we can see what the user pressed to get
  // where they are. Capture phase so we still see it if a handler stops
  // propagation.
  window.addEventListener(
    'click',
    (e) => {
      try {
        const entry = describeClickTarget(e.target)
        if (!entry) return
        interactions.push(entry)
        if (interactions.length > MAX_INTERACTIONS) {
          interactions.splice(0, interactions.length - MAX_INTERACTIONS)
        }
      } catch {
        /* noop */
      }
    },
    true,
  )
}

export function getBufferedConsoleErrors(): BufferedConsoleError[] {
  return consoleErrors.slice()
}

export function clearBufferedConsoleErrors(): void {
  consoleErrors.length = 0
}
